import type { Item } from '../db/schema';

/**
 * Plain case-insensitive substring, over four fields, with no index.
 *
 * A few hundred items filter in well under a millisecond, and an index would
 * be a second copy of the data to keep correct for no gain anyone could feel.
 * If this ever stops being true it will be obvious.
 */

export type Field = 'title' | 'description' | 'notes' | 'source';

export type Match = {
  field: Field;
  /** Where in that field, so the result can highlight it. */
  at: number;
  length: number;
};

/**
 * The first field that hits, in the order a person would read them. Title
 * beats description beats notes beats the shop's domain — a hit in the title
 * is the answer, and showing a snippet of the description underneath it would
 * only be noise.
 */
export function matchItem(item: Item, needle: string): Match | undefined {
  if (needle === '') return undefined;

  return (
    look('title', item.title, needle) ??
    look('description', item.description, needle) ??
    look('notes', item.notes, needle) ??
    look('source', domainOf(item.sourceUrl), needle)
  );
}

export function matches(item: Item, needle: string): boolean {
  return matchItem(item, needle) !== undefined;
}

/** Lowercased once by the caller, then reused across every item. */
export function normalise(query: string): string {
  return query.trim().toLowerCase();
}

/**
 * `www.` dropped: nobody types it, and searching "johnlewis" should find a
 * page saved from `www.johnlewis.com`.
 */
export function domainOf(url: string | undefined): string {
  if (url === undefined) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

export type Snippet = {
  before: string;
  hit: string;
  after: string;
};

/** Enough either side of the hit to read it in context, and no more. */
const AROUND = 40;

export function snippetOf(text: string, match: Match): Snippet {
  const from = Math.max(0, match.at - AROUND);
  const to = Math.min(text.length, match.at + match.length + AROUND);

  return {
    before: (from > 0 ? '…' : '') + text.slice(from, match.at),
    hit: text.slice(match.at, match.at + match.length),
    after: text.slice(match.at + match.length, to) + (to < text.length ? '…' : ''),
  };
}

/** The field's text, for a match that came back from `matchItem`. */
export function textOf(item: Item, field: Field): string {
  switch (field) {
    case 'title':
      return item.title;
    case 'description':
      return item.description;
    case 'notes':
      return item.notes;
    case 'source':
      return domainOf(item.sourceUrl);
  }
}

function look(field: Field, text: string, needle: string): Match | undefined {
  const at = text.toLowerCase().indexOf(needle);
  return at === -1 ? undefined : { field, at, length: needle.length };
}
